import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Tag,
  Input,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const labelIsSelected = (selectedLabels, label) => (
  selectedLabels.some((selectedLabel) => selectedLabel.name === label.name)
);

const visibleLabelsForSearch = (labels, searchValue) => {
  if (!searchValue) return labels;
  const normalizedSearch = searchValue.toLowerCase();
  return labels.filter((label) => label.name.toLowerCase().includes(normalizedSearch));
};

const LabelPickerTray = ({
  addTitle,
  emptySelectedText,
  labels,
  maxAdditionalLabels,
  onClearLabels,
  onDeselectLabel,
  onDisabledLabelClick,
  onManageLabelsClick,
  onSearchChange,
  onSelectLabel,
  searchValue,
  selectedLabels,
  selectedTitle,
  showClear,
  warning,
}) => {
  const allowedAdditionalLabels = maxAdditionalLabels > 0;
  const noLabelsSelected = selectedLabels.length === 0;
  const visibleLabels = visibleLabelsForSearch(labels, searchValue);

  const handleLabelClick = (label) => {
    if (labelIsSelected(selectedLabels, label)) return;
    if (!allowedAdditionalLabels) {
      if (onDisabledLabelClick) onDisabledLabelClick(label);
      return;
    }

    onSelectLabel(label);
  };

  return (
    <>
      <section className="add-label-modal__section">
        <div className="add-label-modal__head">
          <span className="add-label-modal__title">{selectedTitle}</span>
          <span className={`add-label-modal__limit${allowedAdditionalLabels ? '' : ' add-label-modal__limit--full'}`}>
            {allowedAdditionalLabels
              ? `${maxAdditionalLabels} slot${maxAdditionalLabels === 1 ? '' : 's'} left`
              : 'Limit reached'}
          </span>
        </div>
        <div className="add-label-modal__tray">
          {noLabelsSelected
            ? <span className="add-label-modal__empty">{emptySelectedText}</span>
            : selectedLabels.map((label, index) => (
              <Tag
                className="add-label-modal__chip add-label-modal__chip--tray"
                color={label.color}
                closable={true}
                key={label.id || label.name}
                onClose={() => onDeselectLabel(label, index)}
                style={{ color: label.is_dark_text ? 'black' : 'white' }}
              >
                {label.name}
              </Tag>
            ))}
        </div>
        {showClear && (
          <button
            type="button"
            className="add-label-modal__clear"
            onClick={onClearLabels}
            disabled={noLabelsSelected}
          >
            Clear all
          </button>
        )}
        {warning && (
          <p className="add-label-modal__warn">
            {warning}
          </p>
        )}
      </section>

      <section className="add-label-modal__section">
        <div className="add-label-modal__head">
          <span className="add-label-modal__title">{addTitle}</span>
          <Link className="add-label-modal__manage" to="/app/user?tab=manage_labels" onClick={onManageLabelsClick}>
            Manage labels
          </Link>
        </div>
        <Input
          className="add-label-modal__search"
          prefix={<SearchOutlined />}
          placeholder="Search labels"
          allowClear
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          disabled={!allowedAdditionalLabels}
        />
        <div className="add-label-modal__cloud">
          {visibleLabels.length === 0
            ? <span className="add-label-modal__empty">{searchValue ? `No labels match "${searchValue}"` : 'No labels available'}</span>
            : visibleLabels.map((label) => {
              const labelAlreadySelected = labelIsSelected(selectedLabels, label);
              const labelTextColor = label.is_dark_text ? 'black' : 'white';
              let chipClass = 'add-label-modal__chip';
              if (labelAlreadySelected) chipClass += ' add-label-modal__chip--applied';
              else if (!allowedAdditionalLabels) chipClass += ' add-label-modal__chip--disabled';
              return (
                <Tag
                  key={label.id}
                  className={chipClass}
                  onClick={() => handleLabelClick(label)}
                  color={labelAlreadySelected ? 'white' : label.color}
                  style={{
                    color: labelAlreadySelected ? label.color : labelTextColor,
                    border: labelAlreadySelected ? `1px solid ${label.color}` : undefined,
                  }}
                >
                  {label.name}
                </Tag>
              );
            })}
        </div>
      </section>
    </>
  );
};

LabelPickerTray.propTypes = {
  addTitle: PropTypes.string,
  emptySelectedText: PropTypes.node.isRequired,
  labels: PropTypes.array.isRequired,
  maxAdditionalLabels: PropTypes.number.isRequired,
  onClearLabels: PropTypes.func,
  onDeselectLabel: PropTypes.func.isRequired,
  onDisabledLabelClick: PropTypes.func,
  onManageLabelsClick: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSelectLabel: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  selectedLabels: PropTypes.array.isRequired,
  selectedTitle: PropTypes.string.isRequired,
  showClear: PropTypes.bool,
  warning: PropTypes.node,
};

LabelPickerTray.defaultProps = {
  addTitle: 'Add labels',
  onClearLabels: () => {},
  onDisabledLabelClick: null,
  showClear: false,
  warning: null,
};

export default LabelPickerTray;
